/**
 * @fileoverview French translations.
 * @summary French translation strings for KaraoNow frontend.
 *
 * @package KaraoNow
 * @module apps/front/src/utils/i18n/locales/fr
 * @author Pierre-Yves Léglise <support@karaonow.com>
 * @license EULA
 */
export const fr = {
  // Common
  common: {
    loading: 'Chargement...',
    processing: 'Traitement',
    error: 'Erreur',
    success: 'Succès',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    save: 'Enregistrer',
    delete: 'Supprimer',
    edit: 'Modifier',
    create: 'Créer',
    back: 'Retour',
    next: 'Suivant',
    previous: 'Précédent',
    search: 'Rechercher',
    filter: 'Filtrer',
    close: 'Fermer',
    backHome: 'Retour à l\'accueil',
    creating: 'Création en cours...',
    refresh: 'Actualiser',
  },
  // Auth
  auth: {
    login: 'Connexion',
    register: 'Inscription',
    logout: 'Déconnexion',
    email: 'Email',
    password: 'Mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    forgotPassword: 'Mot de passe oublié ?',
    rememberMe: 'Se souvenir de moi',
    loginButton: 'Se connecter',
    registerButton: "S'inscrire",
    alreadyHaveAccount: 'Vous avez déjà un compte ?',
    dontHaveAccount: "Vous n'avez pas de compte ?",
    role: 'Rôle',
    organizer: 'Organisateur',
    singer: 'Chanteur',
    firstName: 'Prénom',
    lastName: 'Nom',
    pseudo: 'Pseudo',
    organization: 'Organisation',
    sendVerificationEmail: 'Envoyer l\'email de vérification',
    emailVerificationSent: 'Email de vérification envoyé',
    emailVerificationInstructions: 'Un email de vérification a été envoyé à {{email}}. Cliquez sur le lien dans l\'email pour finaliser votre inscription.',
    backToLogin: 'Retour à la connexion',
    optional: 'optionnel',
    registerOrganizerTitle: 'Inscription Organisateur',
    registerSingerTitle: 'Inscription Chanteur',
    registerOrganizerDescription: 'Créez votre compte pour organiser des événements karaoke',
    registerSingerDescription: 'Créez votre compte pour participer aux événements karaoke',
    setPassword: 'Créer mot de passe',
    setPasswordTitle: 'Finaliser inscription',
    setPasswordDescription: 'Créez votre mot de passe pour finaliser votre inscription',
    createPassword: 'Créer mon mot de passe',
    passwordRequirements: 'Minimum 12 caractères, avec majuscule, minuscule, chiffre et caractère spécial',
    forgotPasswordDescription: 'Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.',
    sendResetLink: 'Envoyer le lien de réinitialisation',
    resetPasswordEmailSent: 'Email de réinitialisation envoyé',
    resetPasswordInstructions: 'Un email de réinitialisation a été envoyé à {{email}}. Cliquez sur le lien dans l\'email pour réinitialiser votre mot de passe.',
    resetPassword: 'Réinitialiser le mot de passe',
    resetPasswordDescription: 'Créez un nouveau mot de passe pour votre compte',
    resetPasswordButton: 'Réinitialiser mon mot de passe',
    passwordResetSuccess: 'Mot de passe réinitialisé',
    passwordResetSuccessDescription: 'Votre mot de passe a été réinitialisé avec succès. Vous allez être redirigé vers la page de connexion.',
    resetLinkExpired: 'Lien de réinitialisation expiré',
    resetLinkExpiredDescription: 'Ce lien de réinitialisation a expiré (valable 1 heure). Veuillez demander un nouveau lien.',
    requestNewResetLink: 'Demander un nouveau lien',
    rememberPassword: 'Vous vous souvenez de votre mot de passe ?',
  },
  // Organizer menu
  organizerMenu: {
    menu: 'Menu organisateur',
    dashboard: 'Tableau de bord',
    venues: 'Lieux',
    settings: 'Paramètres',
    dataPrivacy: 'Données Personnelles',
    subscriptions: 'Prix',
    about: 'À propos',
    user: 'Utilisateur',
  },
  // Errors
  errors: {
    genericMessage: 'Une erreur est survenue',
    required: 'Ce champ est requis',
    invalidEmail: 'Email invalide',
    passwordTooShort: 'Le mot de passe doit contenir au moins 12 caractères',
    passwordRequirements: 'Le mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial',
    passwordsDoNotMatch: 'Les mots de passe ne correspondent pas',
    loginFailed: 'Échec de la connexion',
    registerFailed: "Échec de l'inscription",
    resetPasswordFailed: 'Échec de la réinitialisation du mot de passe',
    invalidToken: 'Token invalide ou expiré',
    networkError: 'Erreur réseau',
    unauthorized: 'Non autorisé',
    forbidden: 'Accès refusé',
    notFound: {
      title: 'Page introuvable',
      description: 'La page que vous recherchez n\'existe pas ou a été déplacée.',
      explore: 'Découvrir l\'application',
    },
    server: {
      title: 'Erreur serveur',
      description: 'Une erreur inattendue s\'est produite. Veuillez réessayer plus tard.',
    },
    generic: {
      title: 'Oups, quelque chose s\'est mal passé',
      description: 'Une erreur inattendue s\'est produite. Veuillez réessayer.',
    },
    serverError: 'Erreur serveur',
    // Error codes from backend
    VALIDATION_ERROR: 'Erreur de validation',
    CREATE_EVENT_ERROR: 'Erreur lors de la création de l\'événement',
    UPDATE_EVENT_ERROR: 'Erreur lors de la mise à jour de l\'événement',
    EVENT_NOT_FOUND: 'Événement introuvable',
    VENUE_NOT_FOUND: 'Lieu introuvable',
    QUOTA_EXCEEDED_CREATE: 'Quota mensuel atteint pour la création d\'événement',
    QUOTA_EXCEEDED_DATE_CHANGE: 'Quota mensuel atteint pour cette période : Maximum {{limit}} événement(s) par mois. Votre quota sera réinitialisé à la fin de votre période de facturation.',
    SUBSCRIPTION_EVENT_CANNOT_CONVERT: 'Les événements inclus dans votre abonnement ne peuvent pas être convertis en événement unique',
    EVENT_DATE_NOT_REACHED: 'Impossible d\'activer l\'événement avant sa date prévue',
    EVENT_DATE_PAST: 'La date de l\'événement ne peut pas être dans le passé',
    EVENT_DATE_AFTER_EXPIRATION: 'La date de l\'événement ne peut pas être après la date d\'expiration de votre événement unique',
    // Validation errors by field and Zod code
    validation: {
      name: {
        too_small: 'Le nom de l\'événement est requis',
        required: 'Le nom de l\'événement est requis',
        invalid_type: 'Le nom de l\'événement est invalide',
      },
      date: {
        required: 'La date est requise',
        invalid_type: 'La date est invalide',
        invalid_date: 'La date est invalide',
        invalid_string: 'Le format de date est invalide',
      },
      description: {
        invalid_type: 'La description est invalide',
      },
      venueId: {
        invalid_type: 'Le lieu est invalide',
      },
    },
  },
  // Landing page
  landing: {
    languageLabel: 'Choisissez votre langue',
    heroTitle: 'Vos soirées karaoké, simplifiées.',
    heroSubtitle: 'Créez votre événement en 2 minutes. Vos invités scannent un QR code, ajoutent leurs titres — vous gardez la main sur la soirée.',
    ctaPrimary: 'Créer mon premier karaoké — gratuit',
    ctaOrganize: 'Je veux organiser',
    ctaOrganizeDesc: 'Créez des événements, gérez la playlist, suivez les passages.',
    ctaSing: 'Je veux chanter',
    ctaSingDesc: 'Rejoignez un événement via QR code et ajoutez vos titres.',
    alreadyAccount: 'Déjà un compte ?',
    login: 'Connexion',
    whyTitle: 'Pourquoi KaraoNow ?',
    benefit1Title: 'Un QR code, zéro fiche papier',
    benefit1Desc: 'Vos invités scannent, choisissent leurs chansons depuis leur téléphone. Plus de listes à perdre.',
    benefit2Title: 'Playlist en direct',
    benefit2Desc: 'Ordre de passage visible pour tout le monde. Vous enchaînez les titres sans vous prendre la tête.',
    benefit3Title: 'Gratuit pour commencer',
    benefit3Desc: 'Jusqu\'à 4 événements gratuits. Passez en illimité quand vous voulez.',
    twoWaysTitle: 'Deux façons de vivre KaraoNow',
    learnMore: 'En savoir plus',
  },
  // Home page
  home: {
    title: 'Gestion de playlists pour événements Karaoke',
    wantToSing: 'Je veux chanter',
    wantToSingDescription: 'Rejoignez des événements et ajoutez vos chansons',
    wantToOrganize: 'Je veux organiser',
    wantToOrganizeDescription: 'Créez et gérez vos événements karaoke',
    alreadyHaveAccount: 'Vous avez déjà un compte ?',
  },
  // Dashboard
  dashboard: {
    title: 'Tableau de bord',
    welcome: 'Bienvenue',
    events: 'Événements',
    createEvent: 'Créer un événement',
    noEvents: 'Aucun événement',
    noEventsDescription: 'Commencez par créer votre premier événement karaoke',
    filterAll: 'Tous',
    filterDraft: 'Brouillons',
    filterActive: 'Actifs',
    filterCompleted: 'Terminés',
    filterCancelled: 'Annulés',
    loadingEvents: 'Chargement des événements...',
    errorLoadingEvents: 'Erreur lors du chargement des événements',
    noEventsMatchingFilter: 'Aucun événement ne correspond à ce filtre',
    tryDifferentFilter: 'Essayez de sélectionner un autre filtre',
  },
  // Events
  events: {
    name: 'Événement',
    events: 'Événements',
    song: 'Chanson',
    songs: 'Chansons',
    description: 'Description',
    date: 'Date',
    time: 'Heure',
    venue: 'Lieu',
    selectVenue: 'Sélectionner un lieu',
    namePlaceholder: 'Ma soirée karaoké',
    descriptionPlaceholder: 'Description optionnelle...',
    createTitle: 'Créer un événement',
    createSubtitle: 'Créez un nouvel événement karaoké',
    createButton: 'Créer l\'événement',
    createEvent: 'Créer un événement',
    createSuccess: 'Événement créé avec succès',
    createError: 'Erreur lors de la création de l\'événement',
    noVenues: 'Aucun lieu disponible. Veuillez d\'abord créer un lieu.',
    createVenue: 'Créer un lieu',
    status: 'Statut',
    statusDraft: 'Brouillon',
    statusActive: 'Actif',
    statusCompleted: 'Terminé',
    statusCancelled: 'Annulé',
    statusHidden: 'Masqué',
    createdAt: 'Créé le',
    updatedAt: 'Modifié le',
    view: 'Voir',
    open: 'Ouvrir',
    edit: 'Modifier',
    delete: 'Supprimer',
    qrCode: 'QR Code',
    deleteConfirmTitle: 'Supprimer l\'événement',
    deleteConfirm: 'Êtes-vous sûr de vouloir supprimer l\'événement "{{name}}" ?',
    deleteSuccess: 'Événement supprimé avec succès',
    deleteError: 'Erreur lors de la suppression de l\'événement',
    managementTitle: 'Gestion de l\'événement',
    editEvent: 'Modifier l\'événement',
    updateSuccess: 'Événement modifié avec succès',
    updateError: 'Erreur lors de la modification de l\'événement',
    showQRCode: 'Afficher le QR code',
    hideQRCode: 'Masquer le QR code',
    qrCodeTitle: 'Code QR de l\'événement',
    qrCodeDescription: 'Partagez ce code avec vos participants pour qu\'ils puissent rejoindre l\'événement',
    copyQRCode: 'Copier le lien',
    qrCodeCopied: 'Lien copié dans le presse-papiers',
    shareQRCode: 'Partager',
    queue: 'File d\'attente',
    singers: 'Chanteurs',
    noQueue: 'Aucune chanson dans la file d\'attente',
    noSingers: 'Aucun chanteur inscrit',
    guest: 'Invité',
    singer: 'Chanteur',
    songsCount_one: '{{count}} chanson',
    songsCount_other: '{{count}} chansons',
    deleteQueueItem: 'Supprimer la chanson',
    deleteQueueItemConfirm: 'Êtes-vous sûr de vouloir supprimer cette chanson de la file d\'attente ?',
    deleteSinger: 'Supprimer le chanteur',
    deleteSingerConfirm: 'Êtes-vous sûr de vouloir supprimer ce chanteur ? Toutes ses chansons dans la file d\'attente seront également supprimées.',
    singerDeleteError: 'Erreur lors de la suppression du chanteur',
    moveUp: 'Déplacer vers le haut',
    moveDown: 'Déplacer vers le bas',
    dragToReorder: 'Glisser pour réorganiser',
    setCurrent: 'Marquer comme en cours',
    markCompleted: 'Marquer comme terminé',
    cancel: 'Annuler',
    queueUpdateError: 'Erreur lors de la mise à jour de la file d\'attente',
    queueDeleteError: 'Erreur lors de la suppression de la chanson',
    queueStatusPending: 'En attente',
    queueStatusCurrent: 'En cours',
    queueStatusCompleted: 'Terminé',
    queueStatusCancelled: 'Annulé',
    qrCodeUrl: 'URL de l\'événement',
    qrCodeShareText: 'Rejoignez-moi pour cet événement karaoké : {{eventName}} !',
    qrCodePrintTitle: 'QR Code - KaraoNow',
    qrCodePrintInstructions: 'Scannez ce code QR pour accéder à l\'événement et ajouter vos chansons à la file d\'attente.',
    qrCodePrint: 'Imprimer',
    qrCodeShare: 'Partager',
    qrCodeDownload: 'Télécharger',
    startEvent: 'Démarrer l\'événement',
    start: 'Démarrer',
    completeEvent: 'Terminer l\'événement',
    complete: 'Terminer',
    cancelEvent: 'Annuler l\'événement',
    cancelConfirmTitle: 'Confirmer l\'annulation',
    cancelConfirmMessage: 'Êtes-vous sûr de vouloir annuler cet événement ? Cette action est définitive.',
    cancelConfirmOneShotTitle: 'Confirmer l\'annulation - Événement unique',
    cancelConfirmOneShotMessage: '⚠️ Attention : Cet événement unique a été acheté. L\'annulation est définitive et aucun remboursement ne sera effectué si l\'événement n\'a pas été utilisé. Êtes-vous sûr de vouloir annuler cet événement ?',
    cancelConfirmButton: 'Oui, annuler',
    cancelCancelButton: 'Non, garder l\'événement',
    deleteEvent: 'Supprimer l\'événement',
    deleteEventConfirm: 'Êtes-vous sûr de vouloir supprimer cet événement ? Cette action est irréversible.',
    hide: 'Masquer',
    hideConfirmTitle: 'Masquer l\'événement',
    hideConfirm: 'Masquer l\'événement "{{name}}" ? Il ne sera plus visible dans votre liste principale, mais restera comptabilisé dans votre quota mensuel.',
    hideSuccess: 'Événement masqué avec succès',
    hideError: 'Erreur lors du masquage de l\'événement',
    hidden: 'Masqué',
    showHidden: 'Masqués',
    hiddenEventTitle: 'Événement masqué',
    hiddenEventMessage: 'Cet événement a été masqué et n\'est plus accessible. Il reste comptabilisé dans votre quota mensuel.',
    hiddenEventTooltip: 'Cet événement est masqué et ne peut plus être consulté',
    statusUpdated: 'Statut de l\'événement mis à jour',
    backToEvent: 'Retour à l\'événement',
    // Quota exceeded for date change
    quotaExceededDateChange: 'Quota mensuel atteint pour cette date',
    quotaExceededDateChangeTitle: 'Quota mensuel atteint',
    quotaExceededDateChangeExplanation: 'Vous ne pouvez pas changer la date de l\'événement "{{eventName}}" car cela dépasserait votre quota mensuel d\'événements.',
    quotaExceededDateChangeOptions: 'Vous pouvez soit garder la date originale, soit convertir cet événement en "Événement Unique" pour le sortir de votre quota mensuel.',
    quotaExceededDetails: 'Vous avez {{current}} événement(s) sur {{limit}} pour cette période.',
    upgradeToOneShotOption: 'Option : Passer en Événement Unique',
    upgradeToOneShotDescription: 'En convertissant cet événement en "Événement Unique", il ne comptera plus dans votre quota mensuel et vous pourrez changer sa date librement.',
    upgradeToOneShotNow: '🚀 Passer en Événement Unique',
    keepOriginalDate: 'Garder la date originale',
    multipleEventsPremiumSuggestion: 'Plusieurs événements ? Le plan Premium pourrait être plus économique.',
    // Quota exceeded for event creation
    quotaExceededCreateTitle: 'Quota mensuel atteint',
    quotaExceededCreateMessage: 'Vous avez atteint la limite de votre {{planName}}. Vous ne pouvez pas créer plus d\'événements pour cette période.',
    quotaExceededCreateExplanation: 'Votre quota mensuel d\'événements est atteint. Vous pouvez soit attendre la fin de votre période de facturation, soit passer à un plan supérieur.',
    quotaExceededCreateOptions: 'Vous pouvez également créer un événement unique qui ne comptera pas dans votre quota mensuel.',
    // Quota exceeded for FREE plan (no monthly/billing period references)
    quotaExceededCreateTitleFree: 'Quota atteint',
    quotaExceededCreateMessageFree: 'Vous avez atteint la limite de votre {{planName}}. Vous ne pouvez pas créer plus d\'événements.',
    quotaExceededCreateExplanationFree: 'Vous avez atteint la limite d\'événements de votre plan gratuit. Vous pouvez passer à un plan supérieur pour créer plus d\'événements.',
    quotaExceededCreateOptionsFree: 'Vous pouvez également créer un événement unique qui ne comptera pas dans votre quota.',
    quotaExceededDetailsFree: 'Vous avez {{current}} événement(s) sur {{limit}}.',
    createOneShotDescriptionFree: 'Créez un événement unique qui ne comptera pas dans votre quota.',
    upgradePlanDescriptionFree: 'Passez à un plan supérieur pour augmenter votre quota d\'événements.',
    createOneShotOption: 'Option : Créer un Événement Unique',
    createOneShotDescription: 'Créez un événement unique qui ne comptera pas dans votre quota mensuel.',
    createOneShotNow: '🚀 Créer un Événement Unique',
    upgradePlanOption: 'Option : Passer au plan supérieur',
    upgradePlanDescription: 'Passez à un plan supérieur pour augmenter votre quota mensuel d\'événements.',
    upgradePlanNow: 'Passer au plan supérieur',
    quotaResetInfo: 'Votre quota sera réinitialisé à la fin de votre période de facturation.',
    // Generic quota exceeded message
    quotaExceeded: 'Quota mensuel atteint pour cette date',
    // Plan subscription badge
    planSubscription: 'Inclus dans le plan',
    planSubscriptionTooltip: 'Événement inclus dans votre abonnement',
  },
  // Venues
  venues: {
    title: 'Gestion des lieux',
    subtitle: 'Créez et gérez vos lieux de karaoké',
    venuesList: 'Liste des lieux',
    addVenue: 'Ajouter un lieu',
    editVenue: 'Modifier un lieu',
    noVenues: 'Aucun lieu enregistré',
    noVenuesDescription: 'Commencez par créer votre premier lieu',
    eventName: 'Nom de l\'événement',
    eventNamePlaceholder: 'Karaoke de {{firstName}}',
    name: 'Nom du lieu',
    namePlaceholder: 'Ex: Bar Le Chat Noir',
    address: 'Adresse',
    addressPlaceholder: 'Ex: 123 Rue de la Musique',
    city: 'Ville',
    cityPlaceholder: 'Ex: Paris',
    country: 'Pays',
    countryPlaceholder: 'Ex: France',
    googleMapsUrl: 'Lien Google Maps',
    googleMapsUrlPlaceholder: 'https://maps.google.com/...',
    viewOnGoogleMaps: 'Voir sur Google Maps',
    addSuccess: 'Lieu créé avec succès',
    addError: 'Erreur lors de la création du lieu',
    updateSuccess: 'Lieu modifié avec succès',
    updateError: 'Erreur lors de la modification du lieu',
    deleteSuccess: 'Lieu supprimé avec succès',
    deleteError: 'Erreur lors de la suppression du lieu',
    confirmDelete: 'Êtes-vous sûr de vouloir supprimer le lieu "{{name}}" ?',
    confirmDeleteTitle: 'Supprimer le lieu',
    cancel: 'Annuler',
    add: 'Ajouter',
    edit: 'Modifier',
    save: 'Enregistrer',
    deleting: 'Suppression...',
    saving: 'Enregistrement...',
    generateMapsUrl: 'Générer le lien Google Maps',
    generateMapsUrlError: 'Veuillez renseigner au moins le nom du lieu ou la ville pour générer le lien',
    mapsModal: {
      title: 'Sélectionner le lieu sur Google Maps',
      description: 'Utilisez Google Maps pour trouver et ajuster précisément l\'emplacement de votre lieu.',
      instructions: 'Cliquez sur "Ouvrir dans Google Maps" ci-dessous, ajustez le point sur la carte si nécessaire, puis copiez le lien de la barre d\'adresse de votre navigateur et collez-le dans le champ ci-dessous.',
      urlLabel: 'Lien Google Maps',
      openInMaps: 'Ouvrir dans Google Maps',
      confirm: 'Confirmer',
      clickToOpen: 'Cliquez pour ouvrir Google Maps',
      clickDescription: 'Une nouvelle fenêtre s\'ouvrira avec Google Maps pour que vous puissiez ajuster l\'emplacement',
    },
  },
  // Singer interface
  singer: {
    // Dashboard
    dashboard: 'Tableau de bord',
    dashboardSubtitle: 'Visualisez vos événements et vos chansons',
    myEvents: 'Mes événements',
    mySongs: 'Mes chansons',
    allEvents: 'Tous',
    activeEvents: 'Actifs',
    pastEvents: 'Passés',
    inactiveEvents: 'Inactifs',
    sortByDate: 'Trier par date',
    sortByOrganizer: 'Trier par organisateur',
    sortBySongs: 'Trier par nombre de chansons',
    noEvents: 'Vous n\'avez participé à aucun événement.',
    noSongs: 'Vous n\'avez ajouté aucune chanson.',
    eventName: 'Nom de l\'événement',
    organizer: 'Organisateur',
    date: 'Date',
    songsAdded: 'Chansons ajoutées',
    songsSung: 'Chansons chantées',
    status: 'Statut',
    status_active: 'Actif',
    status_completed: 'Complété',
    status_cancelled: 'Annulé',
    status_draft: 'Brouillon',
    title: 'Titre',
    artist: 'Artiste',
    event: 'Événement',
    addedDate: 'Date d\'ajout',
    totalEvents: 'Total d\'événements',
    totalSongs: 'Total de chansons',
    uniqueOrganizers: 'Organisateurs distincts',
    // Settings
    settings: 'Paramètres',
    settingsSubtitle: 'Gérez votre profil et vos données',
    menu: 'Menu chanteur',
    profile: 'Profil',
    changePassword: 'Changer le mot de passe',
    changePasswordDescription: 'Nous vous enverrons un lien pour réinitialiser votre mot de passe par email.',
    sendResetLink: 'Envoyer le lien de réinitialisation',
    passwordResetLinkSent: 'Un lien de réinitialisation a été envoyé à votre adresse email.',
    accountData: 'Données du compte',
    editProfile: 'Modifier mon profil',
    saveChanges: 'Enregistrer les modifications',
    profileUpdated: 'Profil mis à jour avec succès',
    changePasswordTitle: 'Changer mon mot de passe',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    confirmNewPassword: 'Confirmer le nouveau mot de passe',
    passwordRequirements: 'Le mot de passe doit contenir au moins 12 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.',
    passwordChanged: 'Mot de passe modifié avec succès',
    passwordTooShort: 'Le mot de passe doit contenir au moins 12 caractères',
    passwordsNotMatch: 'Les mots de passe ne correspondent pas',
    currentPasswordRequired: 'Le mot de passe actuel est requis',
    // Erreurs de validation du mot de passe
    passwordMinLength: 'Le mot de passe doit contenir au moins 12 caractères',
    passwordNoUppercase: 'Le mot de passe doit contenir au moins une majuscule',
    passwordNoLowercase: 'Le mot de passe doit contenir au moins une minuscule',
    passwordNoDigit: 'Le mot de passe doit contenir au moins un chiffre',
    passwordNoSpecialChar: 'Le mot de passe doit contenir au moins un caractère spécial',
    exportData: 'Exporter mes données',
    exportDataDescription: 'Téléchargez une copie de toutes vos données personnelles au format JSON.',
    downloadData: 'Télécharger mes données',
    dataExported: 'Données exportées avec succès',
    dangerZone: 'Zone dangereuse',
    deleteAccountDescription: 'La suppression de votre compte est irréversible. Toutes vos données seront définitivement supprimées.',
    deleteAccount: 'Supprimer mon compte',
    deleteAccountConfirm: 'Supprimer',
    confirmDeleteAccount: 'Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.',
    // QR Code Landing
    qrCodeMissing: 'Token QR code manquant',
    qrCodeError: 'Erreur lors de la validation du QR code',
    eventNotFound: 'Événement introuvable ou token invalide',
    organizerNotAllowed: 'Vous êtes organisateur. Utilisez votre dashboard pour gérer cet événement.',
    accessDenied: 'Accès non autorisé',
    validatingQRCode: 'Validation du QR code...',
    chooseAccessMethod: 'Choisissez comment rejoindre l\'événement',
    continueAsGuest: 'Continuer en tant qu\'invité',
    orWithAccount: 'ou avec un compte',
    enterPseudo: 'Entrez votre pseudo pour rejoindre l\'événement',
    joinEvent: 'Rejoindre l\'événement',
    joining: 'Connexion...',
    sessionError: 'Erreur lors de la création de la session',
    joinError: 'Erreur lors de la connexion à l\'événement',
    sessionExpired: 'Votre session a expiré. Veuillez scanner à nouveau le QR code de l\'événement.',
    guestMode: 'Mode invité',
    leaveEvent: 'Quitter l\'événement',
    // Singer Event Page
    eventNotActive: 'Cet événement n\'est pas encore actif. Revenez plus tard !',
    eventNotStarted: 'Cet événement n\'a pas encore démarré. Il sera disponible une fois que l\'organisateur aura lancé le karaoké !',
    eventNotYetAvailable: 'Cet événement n\'est pas encore disponible. Veuillez attendre jusqu\'à la date prévue.',
    eventEnded: 'Cet événement est terminé. Les votes ne sont plus acceptés.',
    eventCancelled: 'Cet événement a été annulé. Les votes ne sont pas disponibles.',
    searchSong: 'Rechercher une chanson',
    searchResults: 'Résultats de recherche',
    advancedSearch: 'Recherche avancée',
    advancedSearchToggle: 'Recherche avancée',
    simpleSearch: 'Recherche simple',
    searchPlaceholder: 'Titre ou artiste...',
    titlePlaceholder: 'Titre...',
    artistPlaceholder: 'Artiste...',
    noResults: 'Aucun résultat trouvé',
    addCustomSong: 'Ajouter ce titre',
    addCustomSongDescription: 'Ce titre sera enregistré dans la base et ajouté à votre file d\'attente.',
    addAndQueue: 'Ajouter et mettre en file',
    localResults: '{{count}} résultat(s) trouvé(s) dans notre base',
    musicbrainzResults: '{{count}} résultat(s) MusicBrainz',
    moreResults: 'Plus de résultats',
    addError: 'Erreur lors de l\'ajout à la file d\'attente',
    queueLimitReached: 'Limite atteinte : l\'organisateur de cet événement utilise le plan gratuit qui est limité à {{limit}} titre(s) maximum dans la file d\'attente. La file contient actuellement {{currentCount}} titre(s). Veuillez contacter l\'organisateur si vous souhaitez ajouter plus de titres.',
    songAdded: '"{{title}}" ajouté à la file d\'attente !',
    myQueue: 'Mes chansons',
    noSongsInQueue: 'Vous n\'avez pas encore ajouté de chansons',
    fullQueue: 'File d\'attente',
    queueEmpty: 'La file d\'attente est vide',
    you: 'Vous',
    unknown: 'Inconnu',
    // Queue statuses
    statusPending: 'En attente',
    statusCurrent: 'En cours',
    statusCompleted: 'Terminé',
    statusCancelled: 'Annulé',
  },
  // Singer menu
  singerMenu: {
    about: 'À propos',
    dataPrivacy: 'Données Personnelles',
  },
  // Subscription and Plans
  subscription: {
    title: 'Plans et tarification',
    subtitle: 'Choisissez le plan qui correspond à vos besoins',
    currentPlan: 'Plan actuel',
    planFree: 'Plan Gratuit',
    planOneShot: 'Événement Unique',
    planPro: 'Plan Pro',
    planPremium: 'Plan Premium',
    planUltimate: 'Plan Ultimate',
    planFreeDescription: 'Parfait pour débuter',
    planOneShotDescription: 'Pour un événement',
    planProDescription: 'Pour les organisateurs réguliers',
    planPremiumDescription: 'Pour les passionnés',
    planUltimateDescription: 'Pour les professionnels',
    priceFreeFree: 'Gratuit',
    priceOneShotPer: '{{price}}€ / événement',
    priceProPer: '{{price}}€ / mois',
    pricePremiumPer: '{{price}}€ / mois',
    priceUltimatePer: '{{price}}€ / mois',
    maxEvents: 'Événements maximum',
    maxSongsPerEvent: 'Titres par événement',
    unlimited: 'Illimité',
    features: 'Fonctionnalités',
    currentPlanBadge: 'Plan actuel',
    changePlan: 'Changer de plan',
    upgradePlan: 'Passer au plan premium',
    upgradePlanButton: 'Passer au plan supérieur',
    selectPlan: 'Choisir ce plan',
    monthlyQuota: 'Quota mensuel',
    freeQuota: 'Quota gratuit',
    eventsUsed: '{{current}} / {{limit}} événements',
    billingPeriod: 'Période : {{start}} - {{end}}',
    quotaReached: 'Quota mensuel atteint',
    quotaReachedMessage: 'Vous avez atteint votre limite d\'événements pour cette période. Le quota sera réinitialisé à la fin de votre période de facturation.',
    quotaWarning: 'Quota mensuel presque atteint',
    quotaWarningMessage: 'Il vous reste {{remaining}} événement(s) pour cette période.',
    quotaInfo: 'Il vous reste {{remaining}} événement(s) pour cette période.',
    learnMore: 'En savoir plus',
    contactSupport: 'Contacter le support',
    freePlanNote: 'Le plan gratuit est limité à {{maxEvents}} événements (tous statuts, hors Événement Unique) et {{maxSongsPerEvent}} titres par événement.',
    oneShotPlanNote: 'Nombre illimité de titres dans la playlist de l\'événement. Valide 15 jours.',
    proPlanNote: '{{maxEvents}} événements par mois (période glissante). Titres illimités.',
    premiumPlanNote: '{{maxEvents}} événements par mois (période glissante). Titres illimités.',
    ultimatePlanNote: 'Événements et titres illimités.',
    noEngagement: 'Sans engagement.',
    convertEvent: 'Convertir un événement',
    createEvent: 'Créer un événement',
    chooseOption: 'Choisir une option :',
    convertDescription: 'Sélectionnez un événement gratuit et convertissez-le en plan Événement Unique.',
    createDescription: 'Créez un nouvel événement avec le plan Événement Unique.',
    noFreeEventsToConvert: 'Vous n\'avez pas d\'événement FREE disponible pour convertir.',
    selectFreeEvent: 'Choisissez un événement FREE à convertir :',
    convertThisEvent: 'Convertir cet événement',
    createNewOneShot: 'Créer un nouvel événement',
    newOneShotEventName: 'Nouvel événement unique',
    createEventError: 'Erreur lors de la création de l\'événement',
    createOneShotEventTitle: 'Créer un événement unique',
    proceedToPayment: 'Procéder au paiement',
    // Stripe checkout
    checkoutError: 'Une erreur est survenue lors du paiement',
    paymentSuccessful: 'Paiement réussi !',
    paymentProcessing: 'Votre paiement est en cours de traitement. Vous serez redirigé dans quelques instants.',
    redirectingIn5: 'Redirection automatique dans 5 secondes...',
    backToSubscription: 'Retour aux plans',
    successTitle: 'Paiement confirmé',
    successSubtitle: 'Merci pour votre achat',
    paymentCancelled: 'Paiement annulé',
    checkoutCancelledMessage: 'Vous avez annulé le paiement. Vous pouvez réessayer à tout moment.',
    cancelledTitle: 'Paiement annulé',
    cancelledSubtitle: 'Retournez aux plans pour réessayer',
    whatHappensNext: 'Et ensuite ?',
    webhooksProcessing: 'Nous traitons votre paiement',
    subscriptionActive: 'Votre abonnement sera bientôt actif',
    confirmationEmail: 'Vous recevrez un email de confirmation',
    // Subscription cancellation
    cancelConfirm: 'Êtes-vous sûr de vouloir annuler votre abonnement ?',
    cancelDetails: 'Vous conserverez l\'accès à votre plan Premium jusqu\'à la fin de votre période de facturation actuelle. Après cette date, vous serez basculé au plan Gratuit.',
    cancelSuccess: 'Votre abonnement a été annulé avec succès.',
    cancelPlan: 'Résilier',
    cancellationInProgress: 'L\'annulation de votre abonnement est déjà en cours. Vous serez basculé au plan Gratuit à la fin de votre période de facturation.',
    cancellationInProgressTitle: 'Annulation en cours',
    cancellationInProgressMessage: 'L\'annulation de votre abonnement est déjà en cours. Vous serez basculé au plan Gratuit à la fin de votre période de facturation actuelle. Aucune action supplémentaire n\'est requise.',
    accessUntil: 'Vous aurez accès jusqu\'au {{date}}.',
    cancelError: 'Une erreur est survenue lors de l\'annulation de votre abonnement',
    cancelModalTitle: 'Annuler l\'abonnement',
    cancelModalMessage: 'Vous conserverez l\'accès à votre plan Premium jusqu\'à la fin de votre période de facturation actuelle. Après cette date, vous serez automatiquement basculé au plan Gratuit.',
    cancelModalConfirm: 'Oui, annuler mon abonnement',
    endOfBillingPeriod: 'fin de la période de facturation',
    // Single event upgrade
    upgradeToOneShot: 'Passer en Événement unique',
    upgradeOneShotConfirm: 'Vous êtes sur le point de passer l\'événement "{{eventName}}" en Événement unique.',
    oneShotDescription: 'Il s\'agit d\'un paiement unique pour un nombre illimité de chansons dans cet événement spécifique.',
    price: 'Prix',
    oneTimePayment: 'Paiement unique',
    whatYouGet: 'Ce que vous obtenez :',
    unlimitedSongs: 'Chansons illimitées pour cet événement',
    noLimits: 'Aucune limite de file d\'attente',
    oneShotValidity: 'Valide jusqu\'à la fin de l\'événement',
    upgradeNow: 'Passer maintenant',
    upgradeError: 'Erreur lors de la mise à niveau de l\'événement',
    updateSuccess: 'Abonnement mis à jour avec succès vers le plan {{plan}}',
    changePlanTitle: 'Changer de plan',
    changePlanSubtitle: 'Vous êtes sur le point de modifier votre abonnement',
    newPlan: 'Nouveau plan',
    perMonth: 'mois',
    upgradeCost: 'Coût supplémentaire : +{{amount}}€ / mois',
    downgradeCredit: 'Crédit estimé : -{{amount}}€ / mois',
    immediateChange: 'Changement immédiat',
    immediateChangeDescription: 'Le changement de plan sera effectif immédiatement. Le prorata sera calculé automatiquement par Stripe.',
    confirmChange: 'Confirmer le changement',
    oneShot: 'Événement unique',
    oneShotTooltip: 'Souscription événement unique',
    expiresLabel: 'Expire le',
    oneShotExpirationTitle: 'Expiration de votre événement unique',
    oneShotDaysRemaining: '{{count}} jour restant',
    oneShotDaysRemaining_plural: '{{count}} jours restants',
    oneShotExpiresOn: 'Expire le {{date}}',
    oneShotExpirationWarning: '⚠️ Votre événement repassera en plan gratuit après cette date. Pensez à l\'utiliser avant expiration !',
    oneShotValidityInfo: 'Cet achat est valable {{days}} jours à partir de la date d\'achat. Utilisez-le avant expiration !',
    upgradeToUnlimited: '🚀 Passer en illimité',
    upgradeToUnlimitedNow: '🚀 Passer en illimité maintenant',
    upgradeSingleEventConfirm: 'Vous êtes sur le point de passer l\'événement "{{eventName}}" en illimité.',
    singleEventDescription: 'Il s\'agit d\'un paiement unique pour un nombre illimité de chansons dans cet événement spécifique.',
    singleEventPlan: 'Événement unique',
    multiplePremiumSuggestion: 'Plusieurs événements ? Le plan Premium pourrait être plus avantageux.',
    comparePlans: 'Comparer les plans →',
    premium: 'PREMIUM',
  },
  // Plan alerts
  planAlert: {
    limitReached: 'Limite du plan atteinte',
    songsLimitReached: 'Vous avez atteint la limite du plan gratuit ({{limit}} titre(s)). Pour ajouter plus de titres, souscrivez à un plan payant.',
    eventsLimitReached: 'Vous avez atteint la limite du plan gratuit ({{limit}} événement(s)). Pour créer d\'autres événements, souscrivez à un plan payant.',
    upgrade: 'Souscrire',
    dismissible: true,
  },
  pwa: {
    install: 'Installer',
    installApp: 'Installer l\'application',
    installPrompt: 'Installez KaraoNow pour un accès rapide !',
    installed: 'Application installée',
    offline: 'Vous êtes hors ligne',
    offlineMessage: 'Certaines fonctionnalités peuvent être limitées.',
    updateAvailable: 'Mise à jour disponible',
    updateNow: 'Mettre à jour maintenant',
  },
  // About
  about: {
    title: 'À propos de KaraoNow',
    subtitle: 'Découvrez l\'application qui transforme vos événements karaoke',
    introduction: 'Qu\'est-ce que KaraoNow ?',
    introductionText: 'KaraoNow est une plateforme SaaS innovante conçue pour gérer vos événements karaoke de manière fluide et professionnelle. Que vous soyez organisateur d\'événements ou chanteur passionné, KaraoNow vous offre les outils nécessaires pour créer une expérience karaoke inoubliable.',
    missionText: 'Notre mission est de simplifier la gestion des playlists karaoke et de créer une connexion authentique entre les organisateurs et les chanteurs, en rendant chaque événement mémorable et bien organisé.',
    features: 'Fonctionnalités principales',
    feature1Title: 'Gestion d\'événements',
    feature1Desc: 'Créez et gérez facilement vos événements karaoke avec tous les détails nécessaires.',
    feature2Title: 'Participation interactive',
    feature2Desc: 'Permettez aux chanteurs de rejoindre vos événements via QR code et de voter pour leurs chansons favorites.',
    feature3Title: 'Playlist en temps réel',
    feature3Desc: 'Visualisez et gérez la file d\'attente des chansons en direct avec des mises à jour instantanées.',
    feature4Title: 'Statistiques détaillées',
    feature4Desc: 'Analysez les performances de vos événements avec des graphiques et des métriques en temps réel.',
    feature5Title: 'Sécurité renforcée',
    feature5Desc: 'Vos données sont protégées par des standards de sécurité élevés et une infrastructure cloud robuste.',
    feature6Title: 'Support multilingue',
    feature6Desc: 'KaraoNow supporte 8 langues pour atteindre un public mondial.',
    technology: 'Stack technologique',
    frontend: 'Frontend',
    backend: 'Backend',
    version: 'Version actuelle',
    versionNote: 'Actualisée régulièrement avec de nouvelles fonctionnalités',
    visitWebsite: 'Site officiel',
    support: 'Support',
    supportNote: 'N\'hésitez pas à nous contacter pour toute question',
    copyright: 'Tous droits réservés.',
    privacyNotice: 'Consultez nos pages légales pour en savoir plus sur la protection de vos données.',
    tryForFree: 'Commencez votre essai gratuit',
  },
  // Organizer
  organizer: {
    // Dashboard
    dashboard: 'Tableau de bord',
    dashboardSubtitle: 'Gérez vos événements karaoke',
    menu: 'Menu organisateur',
    // Settings
    settings: 'Paramètres',
    settingsSubtitle: 'Gérez votre profil et vos paramètres',
    editProfile: 'Modifier mon profil',
    saveChanges: 'Enregistrer les modifications',
    profileUpdated: 'Profil mis à jour avec succès',
    emailChangeConfirmTitle: 'Modification de l\'adresse email',
    emailChangeConfirmMessage: 'Un email de confirmation sera envoyé à votre nouvelle adresse email. Votre nouveau email ne sera effectif qu\'après validation du lien dans cet email. Votre ancien email reste actif jusqu\'à la validation.',
    changePassword: 'Changer le mot de passe',
    changePasswordDescription: 'Nous vous enverrons un lien pour réinitialiser votre mot de passe par email.',
    sendResetLink: 'Envoyer le lien de réinitialisation',
    passwordResetLinkSent: 'Un lien de réinitialisation a été envoyé à votre adresse email.',
    accountData: 'Données du compte',
    exportData: 'Exporter mes données',
    exportDataDescription: 'Téléchargez une copie de toutes vos données personnelles au format JSON.',
    downloadData: 'Télécharger mes données',
    dataExported: 'Données exportées avec succès',
    dangerZone: 'Zone dangereuse',
    deleteAccountDescription: 'La suppression de votre compte est irréversible. Toutes vos données seront définitivement supprimées.',
    deleteAccount: 'Supprimer mon compte',
    deleteAccountConfirm: 'Supprimer',
    confirmDeleteAccount: 'Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.',
    // Plan section
    planSection: 'Mon Abonnement',
    planSectionDescription: 'Consultez votre plan actuel, vos limites et vos options de mise à niveau',
    renewDate: 'Renouvellement le ',
    viewPlanDetails: 'Voir les détails du plan',
    eventsLimit: 'Événements créés (hors Événement Unique)',
    subscriptionStatus: 'Statut',
    statusActive: 'Actif',
    statusInactive: 'Inactif',
    statusScheduledEnd: 'Fin programmée',
    subscriptionExpires: 'Expire le',
    subscriptionRenewsOn: 'Se renouvelle le',
    cancellationConfirmed: 'Annulation confirmée !',
    continueAccessUntil: 'Vous continuez à profiter de votre plan Premium jusqu\'au {{date}}',
    autoDowngradeToFree: 'Vous basculerez automatiquement vers le plan Gratuit après cette date.',
    accessUntilEndOfPeriod: 'Vous conservez l\'accès jusqu\'à la fin de votre période de facturation actuelle.',
    planFeatures: 'Fonctionnalités du plan',
    featureEvents: 'Événements par mois',
    featureSongsPerEvent: 'Chansons par événement',
    unlimited: 'Illimité',
    upgradePlan: 'Améliorer mon plan',
    managePlan: 'Gérer mon abonnement',
  },
  // Email change
  email: {
    changeEmail: 'Changer mon adresse email',
    changeEmailDescription: 'Pour modifier votre adresse email, vous devez d\'abord confirmer la nouvelle adresse. Un email de confirmation sera envoyé à la nouvelle adresse.',
    currentEmail: 'Email actuel',
    newEmail: 'Nouvelle adresse email',
    newEmailAddress: 'Nouvelle adresse',
    requestEmailChange: 'Demander un changement d\'email',
    emailRequired: 'L\'adresse email est requise',
    emailUnchanged: 'La nouvelle adresse email doit être différente de l\'actuelle',
    emailChangeConfirmationSent: 'Un email de confirmation a été envoyé à votre nouvelle adresse email.',
    emailChangeInfo: 'Votre ancien email reste actif jusqu\'à la validation de la nouvelle adresse.',
    verifying: 'Vérification en cours',
    verifyingDescription: 'Vérification de votre adresse email...',
    success: 'Email confirmé',
    confirmationSuccess: 'Votre adresse email a été changée avec succès !',
    confirmationFailed: 'Échec de la confirmation',
    linkExpired: 'Lien expiré',
    tokenExpired: 'Le lien de confirmation a expiré. Veuillez demander un nouveau lien.',
    linkExpiredDescription: 'Le lien de confirmation a expiré (valide 24 heures). Veuillez demander un nouveau lien de confirmation.',
    redirecting: 'Redirection en cours...',
    redirectingToLogin: 'Redirection vers la connexion dans 3 secondes...',
    goToSettings: 'Aller aux paramètres',
    backToSettings: 'Retour aux paramètres',
    requestNewLink: 'Demander un nouveau lien',
    missingToken: 'Token de confirmation manquant.',
    validationPending: 'Validation en attente',
    pendingEmailAddress: 'En attente de validation',
  },
  // RGPD - Data Privacy
  rgpd: {
    pageTitle: 'Données Personnelles',
    pageSubtitle: 'Gérez vos données et votre compte de manière sécurisée',
    exportData: 'Télécharger Mes Données',
    exportDataDescription: 'Téléchargez une copie complète de vos données personnelles au format JSON. Vous pouvez utiliser ces données pour les importer ailleurs ou simplement les conserver.',
    exportIncludes: 'Cet export inclut',
    exportProfile: 'Profil et préférences',
    exportHistory: 'Historique des événements',
    exportEvents: 'Événements créés',
    exportSubscription: 'Informations d\'abonnement',
    downloadData: 'Télécharger (JSON)',
    exporting: 'Téléchargement en cours...',
    exportFormatJSON: 'Format: JSON (compatible RGPD)',
    dataExportedSuccess: '✅ Vos données ont été téléchargées avec succès !',
    deleteAccount: 'Supprimer Mon Compte',
    deleteAccountDescription: 'Cette action supprimera définitivement votre compte et toutes vos données associées. Cette opération ne peut pas être annulée.',
    deleteWarning: '⚠️ Avertissement Important',
    deleteIrreversible: 'Cette action est définitive et irréversible',
    deleteAllData: 'Toutes vos données seront supprimées',
    deleteNoRecovery: 'Il n\'y aura aucune possibilité de récupération',
    deleteRecommendation: 'Nous recommandons de télécharger vos données avant de supprimer votre compte.',
    deleteAccountButton: 'Supprimer Mon Compte',
    confirmDeletion: 'Confirmer la suppression',
    deleteConfirmMessage: 'Êtes-vous sûr de vouloir supprimer votre compte ? Cette action ne peut pas être annulée.',
    continueDelete: 'Continuer vers la suppression',
    finalDeleteWarning: 'Dernière Confirmation - Pas de Retour',
    finalDeleteMessage: 'C\'est votre dernière chance ! Cliquer sur "Supprimer définitivement" supprimera votre compte de manière irréversible.',
    permanentlyDelete: 'Supprimer Définitivement',
    gdprCompliance: 'Conformité RGPD',
    gdprInfo: 'Conformément à la Loi RGPD, vous avez le droit d\'accès, de rectification, d\'effacement, et de portabilité de vos données personnelles. Ces outils vous permettent d\'exercer ces droits.',
  },
  // Cookies consent
  cookies: {
    title: '🍪 Nous utilisons des cookies',
    description: 'Nous utilisons des cookies pour améliorer votre expérience. Consultez notre politique pour plus d\'informations.',
    understood: 'J\'ai compris',
    learnMore: 'En savoir plus',
    footerNote: 'Vous pouvez consulter notre Politique des cookies pour plus de détails.',
  },
  // Cookies policy page
  cookiesPage: {
    title: 'Politique des Cookies',
    subtitle: 'Informations complètes sur les cookies utilisés sur KaraoNow',
    overview: 'Aperçu',
    overviewText: 'KaraoNow utilise des cookies pour améliorer votre expérience utilisateur, sécuriser votre compte et comprendre comment vous utilisez notre plateforme. Cette politique explique comment et pourquoi nous utilisons les cookies.',
    whatAreCookies: 'Qu\'est-ce que les cookies ?',
    whatAreCookiesText: 'Les cookies sont de petits fichiers texte stockés sur votre appareil. Ils permettent aux sites web de mémoriser des informations sur vos visites, comme vos préférences et données de connexion.',
    typesOfCookies: 'Types de Cookies',
    sessionCookies: 'Cookies de Session',
    sessionCookiesText: 'Ces cookies expirent quand vous fermez votre navigateur. Ils sont utilisés pour maintenir votre session actif et sécuriser votre accès.',
    persistentCookies: 'Cookies Persistants',
    persistentCookiesText: 'Ces cookies restent stockés sur votre appareil pendant une période définie. Ils nous permettent de vous reconnaître lors de vos prochaines visites.',
    howWeUseCookies: 'Comment nous utilisons les Cookies',
    useAuthentication: 'Authentification et sécurité de session',
    usePreferences: 'Mémorisation de vos préférences (langue, thème)',
    useSecurity: 'Protection contre les abus et les attaques',
    usePerformance: 'Amélioration des performances de la plateforme',
    yourRights: 'Vos Droits',
    yourRightsText: 'Vous avez le droit de contrôler les cookies. Vous pouvez accepter ou refuser les cookies non essentiels via notre banneau de consentement.',
    managingCookies: 'Gérer les Cookies',
    managingCookiesText: 'Vous pouvez gérer les cookies via les paramètres de votre navigateur. Notez que désactiver certains cookies peut affecter le fonctionnement de KaraoNow.',
    contact: 'Contact',
    contactText: 'Pour toute question concernant cette politique, veuillez nous contacter via notre page de contact ou nos conditions d\'utilisation.',
    footer: 'Avez-vous des questions ? Retournez à l\'accueil.',
  },
  footer: {
    legal: 'Mentions légales',
    privacy: 'Politique de confidentialité',
    cookies: 'Politique des cookies',
    terms: 'Conditions d\'utilisation',
  },
  // Legal mentions page
  legalPage: {
    title: 'Mentions Légales',
    subtitle: 'Informations légales sur KaraoNow',
    editor: {
      title: 'Éditeur du site',
      name: 'axialdata',
      address: 'Impasse du pélican, 97233 Schoelcher, Martinique',
      phone: 'Téléphone : +596696367741',
      email: 'Email : contact@axialdata.net',
      siret: 'SIRET : 49347625300023',
    },
    publication: {
      title: 'Directeur de la publication',
      director: 'Pierre-Yves Léglise',
    },
    hosting: {
      title: 'Hébergeur',
    },
    dataProtection: {
      title: 'Protection des données',
      description: 'Pour en savoir plus sur la protection de vos données personnelles, veuillez consulter notre politique de confidentialité.',
      link: 'Lire notre politique de confidentialité',
    },
  },
  // Privacy policy page
  privacyPage: {
    title: 'Politique de Confidentialité',
    subtitle: 'Comment nous protégeons vos données personnelles',
    lastUpdated: 'Dernière mise à jour : 1er janvier 2025',
    intro: {
      title: 'Introduction',
      description: 'KaraoNow s\'engage à protéger votre vie privée. Cette politique explique comment nous collectons, utilisons et protégeons vos données personnelles conformément à la RGPD.',
    },
    dataCollection: {
      title: 'Données Collectées',
      organizers: {
        title: 'Pour les Organisateurs',
        email: 'Adresse email',
        name: 'Prénom, nom et organisation',
        organization: 'Détails de l\'organisation',
        payment: 'Informations de paiement (via processeur tiers)',
        events: 'Informations sur les événements créés',
      },
      singers: {
        title: 'Pour les Chanteurs',
        email: 'Adresse email (optionnelle)',
        pseudo: 'Pseudo d\'utilisateur',
        name: 'Prénom et nom (optionnel)',
        participation: 'Événements auxquels vous avez participé',
        history: 'Historique des chansons ajoutées à la queue',
      },
    },
    purposes: {
      title: 'Utilisation de vos données',
      authentication: 'Authentification et gestion de compte',
      eventManagement: 'Création et gestion d\'événements',
      songManagement: 'Gestion de la queue de chansons',
      communication: 'Communications importantes concernant votre compte',
      billing: 'Traitement des paiements et facturation',
      support: 'Support client et assistance technique',
      preferences: 'Amélioration de votre expérience utilisateur',
    },
    legalBasis: {
      title: 'Base légale du traitement',
      description: 'Le traitement de vos données est basé sur votre consentement lors de l\'inscription et l\'exécution du contrat de service entre vous et KaraoNow.',
    },
    retention: {
      title: 'Durée de conservation',
      active: 'Données actives : conservées aussi longtemps que votre compte est actif',
      inactive: 'Données inactives : supprimées 12 mois après la dernière connexion',
      legal: 'Données légales : conservées aussi longtemps que requis par la loi',
    },
    rights: {
      title: 'Vos droits RGPD',
      description: 'Conformément à la RGPD, vous disposez des droits suivants :',
      access: 'Droit d\'accès à vos données personnelles',
      rectification: 'Droit de rectification de vos données',
      deletion: 'Droit à l\'oubli (suppression de vos données)',
      portability: 'Droit à la portabilité de vos données',
      opposition: 'Droit d\'opposition au traitement',
      limitation: 'Droit à la limitation du traitement',
      contact: 'Pour exercer ces droits, contactez-nous à support@karaonow.com',
    },
    security: {
      title: 'Sécurité',
      description: 'Nous utilisons des mesures de sécurité appropriées pour protéger vos données contre l\'accès non autorisé, la modification ou la divulgation.',
    },
    cookies: {
      title: 'Cookies',
      description: 'KaraoNow utilise des cookies pour améliorer votre expérience utilisateur. Pour en savoir plus, consultez notre politique des cookies.',
      link: 'Lire notre politique des cookies',
    },
    contact: {
      title: 'Nous contacter',
      description: 'Pour toute question concernant cette politique ou vos données personnelles, veuillez nous contacter :',
      email: 'support@karaonow.com',
    },
  },
  // Terms of Service page
  termsPage: {
    title: 'Conditions Générales d\'Utilisation',
    subtitle: 'Termes et conditions de KaraoNow',
    lastUpdated: 'Dernière mise à jour : 1er janvier 2025',
    intro: {
      title: 'Introduction',
      description: 'Bienvenue sur KaraoNow ! En utilisant notre plateforme, vous acceptez les présentes conditions générales d\'utilisation. Veuillez les lire attentivement.',
    },
    service: {
      title: 'Description du service',
      description: 'KaraoNow est une plateforme SaaS permettant de gérer des événements karaoke. La plateforme offre deux interfaces principales :',
      organizers: {
        title: 'Pour les organisateurs',
        description: 'Les organisateurs peuvent créer des événements, générer des QR codes pour partager avec les chanteurs, gérer une queue de chansons, et modifier l\'ordre de passage des chanteurs. Un système d\'abonnement propose trois plans : gratuit (limité), événement unique, et premium (abonnement mensuel).',
      },
      singers: {
        title: 'Pour les chanteurs',
        description: 'Les chanteurs peuvent rejoindre des événements via QR code, ajouter des chansons à la queue de l\'événement, et visualiser l\'ordre des chansons. Les chanteurs peuvent participer sans compte (mode invité) ou avec un compte pour bénéficier de fonctionnalités avancées.',
      },
    },
    accounts: {
      title: 'Comptes utilisateur',
      description: 'Pour utiliser KaraoNow, vous devez créer un compte. Vous êtes responsable de maintenir la confidentialité de vos identifiants.',
      responsibility: 'Vous êtes responsable de toutes les activités effectuées via votre compte',
      accuracy: 'Vous vous engagez à fournir des informations exactes et à jour',
      security: 'Vous acceptez de maintenir la sécurité de votre mot de passe',
      notTransferable: 'Votre compte est personnel et non transférable',
    },
    subscription: {
      title: 'Plans d\'abonnement',
      description: 'KaraoNow propose plusieurs formules d\'abonnement pour les organisateurs :',
      free: 'Plan gratuit : 4 événements maximum (tous statuts confondus, hors événements uniques), 15 chansons par événement',
      oneShot: 'Événement unique : paiement unique pour un événement spécifique, nombre illimité de chansons, validité limitée dans le temps',
      premium: 'Plans payants avec abonnement mensuel : plusieurs formules disponibles avec des quotas mensuels d\'événements, nombre illimité de chansons par événement, renouvellement automatique mensuel',
    },
    payment: {
      title: 'Conditions de paiement',
      description: 'Le paiement est traité via un processeur de paiement tiers sécurisé.',
      currency: 'Les paiements sont effectués en euros (EUR)',
      noRefund: 'Aucun remboursement n\'est accordé pour les services déjà fournis',
      cancellation: 'Vous pouvez annuler votre abonnement à tout moment',
      automaticRenewal: 'Les abonnements mensuels se renouvellent automatiquement chaque mois sauf annulation',
    },
    conduct: {
      title: 'Comportement des utilisateurs',
      description: 'Vous vous engagez à utiliser KaraoNow de manière légale et éthique.',
      prohibited: {
        title: 'Actions interdites',
        illegal: 'Toute activité illégale ou violant les lois applicables',
        harmful: 'Contenu nuisible, offensant ou diffamatoire',
        harassing: 'Harcèlement, intimidation ou discrimination',
        spam: 'Envoi de spam ou d\'autres communications non désirées',
        fraud: 'Tentatives de fraude ou d\'escroquerie',
      },
    },
    organizerResponsibilities: {
      title: 'Responsabilités des organisateurs',
      description: 'Les organisateurs acceptent les responsabilités suivantes :',
      songSelection: 'Vous êtes responsable de la légalité du contenu des chansons',
      eventManagement: 'Vous devez gérer votre événement conformément à ces conditions',
      dataManagement: 'Vous êtes responsable de la gestion des données des chanteurs',
      copyrightCompliance: 'Vous vous engagez à respecter les droits d\'auteur des chansons',
    },
    singerRights: {
      title: 'Droits et responsabilités des chanteurs',
      description: 'Les chanteurs disposent des droits et acceptent les responsabilités suivantes :',
      participation: 'Vous pouvez participer à des événements organisés par d\'autres',
      songSelection: 'Vous pouvez ajouter des chansons à la queue de l\'événement',
      noDuplication: 'Vous ne pouvez pas ajouter la même chanson plusieurs fois',
      respect: 'Vous acceptez de respecter les règles établies par l\'organisateur',
    },
    intellectualProperty: {
      title: 'Propriété intellectuelle',
      description: 'Tous les droits de propriété intellectuelle sur KaraoNow et son contenu (sauf les chansons musicales) appartiennent à KaraoNow. Vous ne pouvez pas reproduire ou utiliser le contenu sans permission.',
    },
    liability: {
      title: 'Limitation de responsabilité',
      description: 'KaraoNow est fourni \"en l\'état\". Nous ne sommes pas responsables des dommages directs ou indirects résultant de l\'utilisation de la plateforme. Notre responsabilité est limitée au maximum au montant que vous avez payé pour l\'abonnement au cours du dernier mois.',
    },
    termination: {
      title: 'Résiliation',
      description: 'KaraoNow se réserve le droit de suspendre ou de résilier votre compte en cas de violation de ces conditions. Vous pouvez résilier votre compte à tout moment.',
    },
    modifications: {
      title: 'Modifications des conditions',
      description: 'KaraoNow se réserve le droit de modifier ces conditions à tout moment. Les modifications entrent en vigueur 30 jours après la notification. Votre utilisation continue de la plateforme après cette période signifie votre acceptation des nouvelles conditions.',
    },
    jurisdiction: {
      title: 'Juridiction',
      description: 'Ces conditions sont régies par la loi française. Tout litige sera soumis aux tribunaux compétents en France.',
    },
    contact: {
      title: 'Contact',
      description: 'Pour toute question concernant ces conditions, veuillez nous contacter :',
      email: 'support@karaonow.com',
    },
  },
}
